import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Game } from '@tinymint/shared';

type SortOption = 'new' | 'trending' | 'popular';

function Gallery() {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('new');
  const navigate = useNavigate();

  useEffect(() => {
    fetchGames();
  }, [sort]);

  const fetchGames = async (searchQuery?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (searchQuery) params.set('search', searchQuery);

      const response = await fetch(`/.proxy/api/games?${params}`);
      if (response.ok) {
        const data = await response.json();
        setGames(data.games);
      }
    } catch (error) {
      console.error('Failed to fetch games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchGames(search);
  };

  const handleGameClick = (gameId: string) => {
    navigate(`/play/${gameId}`);
  };

  return (
    <div>
      {/* Search and Sort */}
      <form className="search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          className="input"
          placeholder="Search games..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="btn">Search</button>
      </form>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {(['new', 'trending', 'popular'] as SortOption[]).map((option) => (
          <button
            key={option}
            className={`nav-btn ${sort === option ? 'active' : ''}`}
            onClick={() => setSort(option)}
          >
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </button>
        ))}
      </div>

      {/* Games Grid */}
      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
        </div>
      ) : games.length === 0 ? (
        <div className="loading">
          <p>No games found. Be the first to generate one!</p>
        </div>
      ) : (
        <div className="game-grid">
          {games.map((game) => (
            <div
              key={game.id}
              className="game-card"
              onClick={() => handleGameClick(game.id)}
            >
              <div className="game-card-image">
                {game.thumbnail_url ? (
                  <img src={game.thumbnail_url} alt={game.prompt} />
                ) : (
                  <span style={{ fontSize: '3rem' }}>🎮</span>
                )}
              </div>
              <div className="game-card-info">
                <div className="game-card-title" title={game.prompt}>
                  {game.prompt}
                </div>
                <div className="game-card-meta">
                  ▶ {game.play_count.toLocaleString()} plays
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Gallery;
